import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Drawer, Switch, useDrawer } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { useTranslation } from 'react-i18next'

import { useManageApiUsage } from '../hooks/useManageApiUsage'
import {
  ManageApiUsageForm,
  ManageApiUsageSchema,
} from '../schemas/ManageApiUsageSchema'
import { useSelectedApplicationStore } from '../stores/useSelectedApplicationStore'

export function DrawerManageApiUsage() {
  const { t } = useTranslation(['sso', 'common'])
  const { close } = useDrawer('drawer-manage-api-usage')
  const { application } = useSelectedApplicationStore()
  const { handleManageApiUsage, isManagingApiUsage } = useManageApiUsage()

  const hookForm = useForm<ManageApiUsageForm>({
    resolver: zodResolver(ManageApiUsageSchema()),
    defaultValues: {
      apiUser: false,
    },
  })

  const handleSubmit = async (data: ManageApiUsageForm) => {
    if (!application?.id) return

    await handleManageApiUsage(application.id, data.apiUser).then(() => {
      close()
    })
  }

  const handleCancelManageApiUsage = () => {
    hookForm.reset()
    close()
  }

  useEffect(() => {
    if (application) {
      hookForm.reset({
        apiUser: application.requiresApiUser || false,
      })
    }
  }, [application, hookForm])

  return (
    <Drawer.Root id="drawer-manage-api-usage">
      <Drawer.Header>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('sso:applications.drawer_manage_api_usage.header.title')}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <h2 className="text-2xl font-semibold text-brand-700">
          {t('sso:applications.drawer_manage_api_usage.body.title')}
        </h2>

        <FormProvider {...hookForm}>
          <form
            className="space-y-6"
            onSubmit={hookForm.handleSubmit(handleSubmit)}
          >
            <Switch.Root className="flex justify-between">
              <Switch.Label htmlFor="apiUser">
                {t(
                  'sso:applications.drawer_manage_api_usage.fields.requires_api_user.label',
                )}
              </Switch.Label>
              <Switch.Trigger name="apiUser" />
            </Switch.Root>
          </form>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer>
        <div className="flex items-center justify-end gap-4">
          <Button.Root
            type="button"
            variant="secondary"
            onClick={handleCancelManageApiUsage}
            disabled={isManagingApiUsage}
          >
            {t(
              'sso:applications.drawer_manage_api_usage.footer.close_button.label',
            )}
          </Button.Root>

          <Button.Root
            type="button"
            onClick={hookForm.handleSubmit(handleSubmit)}
            isLoading={isManagingApiUsage}
            disabled={isManagingApiUsage}
          >
            {t(
              'sso:applications.drawer_manage_api_usage.footer.submit_button.label',
            )}
          </Button.Root>
        </div>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
